import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import JobStatusBadge from './JobStatusBadge';
import { useOrgTimezone, formatDateTimeInTz } from '@/utils/timezone';
import { ClipboardList, Calendar, Users, MapPin, Eye, Play, CheckCircle, Wrench } from 'lucide-react';
import type { JobListItem } from '../../types/jobs';

interface JobCardProps {
  job: JobListItem;
  currentUserId?: number;
  onStart?: (id: number) => void;
}

function JobCard({ job, currentUserId, onStart }: JobCardProps) {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();

  const isAssigned = currentUserId && job.assignments?.some(a => a.user_id === currentUserId);
  const userAssignment = job.assignments?.find(a => a.user_id === currentUserId);
  const canStart = isAssigned && userAssignment?.status === 'assigned';
  const hasEquipment = job.equipment && job.equipment.length > 0;

  const assigneeNames = job.assignments?.map(a => `${a.first_name} ${a.last_name}`).join(', ') || '—';
  const primaryEquip = job.equipment?.find(e => e.role === 'primary');
  const implements_ = job.equipment?.filter(e => e.role === 'implement') || [];

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <JobStatusBadge status={job.status} size="sm" />
              {job.activity_type_name && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 truncate">
                  {job.activity_type_name}
                </span>
              )}
              {job.coverage_pct != null && (
                <span className="text-xs text-neutral-500">{Number(job.coverage_pct).toFixed(0)}% covered</span>
              )}
            </div>
            <h3 className="text-base font-semibold text-neutral-900 truncate">{job.name}</h3>
            {job.description && (
              <p className="text-sm text-neutral-500 truncate">{job.description}</p>
            )}
          </div>
          <div className="flex-shrink-0 ml-3">
            <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-indigo-600" />
            </div>
          </div>
        </div>

        {/* Target */}
        {job.target_name && (
          <p className="text-xs text-neutral-500 flex items-center gap-1 mb-2 truncate">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
            {job.target_name}
            {job.target_area_hectares != null && ` (${Number(job.target_area_hectares).toFixed(1)} ha)`}
          </p>
        )}

        {/* Equipment */}
        {hasEquipment && (
          <div className="text-xs text-neutral-500 flex items-center gap-1 mb-2">
            <Wrench className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">
              {primaryEquip?.name}
              {implements_.length > 0 && ` + ${implements_.map(i => i.name).join(', ')}`}
            </span>
          </div>
        )}

        {/* Schedule & assignees */}
        <div className="grid grid-cols-2 gap-2 text-xs text-neutral-500 mb-3">
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {job.scheduled_start
              ? formatDateTimeInTz(job.scheduled_start, orgTz)
              : '—'}
            {job.scheduled_end && (
              <span> — {formatDateTimeInTz(job.scheduled_end, orgTz)}</span>
            )}
          </div>
          <div className="flex items-center gap-1 truncate">
            <Users className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{assigneeNames}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-3 border-t border-neutral-100">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/jobs/${job.id}`)}
            className="flex-1"
          >
            <Eye className="h-4 w-4" />
            View
          </Button>

          {canStart && onStart && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onStart(job.id)}
              className="flex-1"
            >
              <Play className="h-4 w-4" />
              Start
            </Button>
          )}

          {isAssigned && userAssignment?.status === 'in_progress' && (
            <Button
              size="sm"
              onClick={() => navigate(`/jobs/${job.id}/operate`)}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
            >
              <CheckCircle className="h-4 w-4" />
              Continue
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default JobCard;
